import { useFlags } from 'launchdarkly-react-client-sdk';
import { useGetPage } from './useGetPage';
import { pageIds } from 'utilities/constants';
import {
  LOGOUT,
  MY_ACCOUNT_PROFILE,
  TEST_KITS_MANAGEMENT_TEST_KITS,
  PROVIDERS_FIND_A_PROVIDER,
  PROVIDERS_PAST_PROVIDERS,
  PROVIDERS,
  MY_SKIN_MY_VISIT_SUMMARIES,
  MY_SKIN_MY_MEDICATION_HISTORY,
  MY_PROFILE_ACCOUNT_SETTINGS,
  MY_APPOINTMENTS,
  MY_APPOINTMENTS_SCHEDULE_AN_APPOINTMENT,
  SHOP,
  TEST_KITS_MANAGEMENT,
  ORDERS,
  SUPER_ADMIN_TESTS,
  SUPER_ADMIN_PAYMENTS,
  SUPER_ADMIN_ADMINS,
  SUPER_ADMIN_PATIENTS,
  SUPER_ADMIN_PROVIDERS,
  SUPER_ADMIN_ORDERS,
  SUPER_ADMIN_PRODUCTS,
} from 'utilities/routes';
import { CodexFeatureFlags } from 'utilities/interfaces';
import { transformNavBarOptionsToLowercase } from 'utilities/functions';
import { FIND_A_PROVIDER } from '../app/my-appointments/components/scheduled-a-appointment/routePath';
import { useAuth } from '../auth/context/AuthContext';
import { PATIENT_PROFILE_ROUTES } from '../app/my-account/pages/patient/constants';
import { AdminPermissionType } from '../graphql/generated/hasura';

export const useNavbarOptions = () => {
  const { enableDermscoreTabs } = useFlags<CodexFeatureFlags>();
  const { data: locale, loading } = useGetPage({
    locale: 'en',
    pageId: pageIds.NAVBAR_COMPONENT,
  });
  const { selectedAccount } = useAuth();

  const MySkinOptions: string[] = [
    ...(enableDermscoreTabs ? [locale?.mySkinOptions.myVisitSummaries] : []),
    ...(enableDermscoreTabs ? [locale?.mySkinOptions.myMedicationHistory] : []),
  ].map((option) => transformNavBarOptionsToLowercase(option));

  const ProvidersOptions: string[] = [
    locale?.providersOptions?.myProviders,
    locale?.providersOptions?.pastProviders,
    locale?.providersOptions?.findAProvider,
  ];

  const ManageTestKitsOptions: string[] = [
    locale?.manageTestKitsOptions.manageTestKits,
    locale?.manageTestKitsOptions.shopTestKits,
    locale?.manageTestKitsOptions.myOrders,
  ];

  const AppointmentsOptions: string[] = [
    locale?.appointmentsOptions?.myAppointments,
    locale?.appointmentsOptions?.scheduleAnAppointment,
    !selectedAccount && 'Receipts history',
  ]
    .filter(Boolean)
    .map((option) => transformNavBarOptionsToLowercase(option));

  const ShopOptions: string[] = [
    locale?.shopOptions.codexProducts,
    locale?.shopOptions.shopAcne,
    locale?.shopOptions.shopTestKits,
    locale?.shopOptions.shopSupplements,
    locale?.shopOptions.shopEczema,
    locale?.shopOptions.shopPsoriasis,
    locale?.shopOptions.newShop,
  ].map((option) => transformNavBarOptionsToLowercase(option));

  const MyAccountOptions: string[] = [
    locale?.myAccountOptions.myProfile,
    locale?.myAccountOptions.healthQuestionnaire,
    locale?.myAccountOptions.logOut,
  ];

  const SuperAdminAccountOptions: string[] = [
    locale?.superAdminAccountOptions?.changePassword,
    locale?.superAdminAccountOptions?.logOut,
  ];

  const ProviderMyAccountOptions: string[] = [
    locale?.myAccountOptions.myProfile,
    locale?.myAccountOptions.logOut,
  ].map((option) => option);

  const ProviderNotApprovedMyAccountOptions: string[] = [
    locale?.myAccountOptions.logOut,
  ].map((option) => transformNavBarOptionsToLowercase(option));

  const AdminProductsOptions: string[] = [
    locale?.products ?? 'Products',
    locale?.testKits ?? 'Test Kits',
    locale?.orders ?? 'Orders',
    locale?.payments ?? 'Payments',
  ].map((option) => transformNavBarOptionsToLowercase(option));

  const AdminUsersOptions: string[] = [
    locale?.admins ?? 'Users',
    locale?.patients ?? 'Patients',
    locale?.providers ?? 'Providers',
  ].map((option) => transformNavBarOptionsToLowercase(option));

  const MySkinRoutes: Record<string, string> = {
    [locale?.mySkinOptions.myVisitSummaries]: MY_SKIN_MY_VISIT_SUMMARIES,
    [locale?.mySkinOptions.myMedicationHistory]: MY_SKIN_MY_MEDICATION_HISTORY,
  };

  const ManageTestKitsRoutes: Record<string, string> = {
    [locale?.manageTestKitsOptions.manageTestKits]:
      TEST_KITS_MANAGEMENT_TEST_KITS,
    [locale?.manageTestKitsOptions
      .shopTestKits]: `${TEST_KITS_MANAGEMENT}${SHOP}`,
    [locale?.manageTestKitsOptions
      .myOrders]: `${TEST_KITS_MANAGEMENT}${ORDERS}`,
  };

  const ShopRoutes: Record<string, string> = {
    [transformNavBarOptionsToLowercase(locale?.shopOptions.codexProducts)]: '',
    [transformNavBarOptionsToLowercase(locale?.shopOptions.shopTestKits)]:
      'collections/diagnostics',
    [transformNavBarOptionsToLowercase(locale?.shopOptions.shopAcne)]:
      'collections/shaant',
    [transformNavBarOptionsToLowercase(locale?.shopOptions.shopSupplements)]:
      'collections/supplements',
    [transformNavBarOptionsToLowercase(locale?.shopOptions.shopEczema)]:
      'collections/bia',
    [transformNavBarOptionsToLowercase(locale?.shopOptions.shopPsoriasis)]:
      'collections/antu',
    [transformNavBarOptionsToLowercase(locale?.shopOptions.newShop)]: SHOP,
  };

  const MyAccountRoutes: Record<string, string> = {
    [locale?.myAccountOptions.myProfile]: MY_ACCOUNT_PROFILE,
    [locale?.myAccountOptions
      .healthQuestionnaire]: `${MY_ACCOUNT_PROFILE}/${PATIENT_PROFILE_ROUTES.HEALTH_QUESTIONNAIRE}`,
    [locale?.myAccountOptions.logOut]: LOGOUT,
  };

  const SuperAdminAccountRoutes: Record<string, string | null> = {
    [locale?.superAdminAccountOptions?.changePassword]: null,
    [locale?.superAdminAccountOptions?.logOut]: LOGOUT,
  };

  const ProviderMyAccountRoutes: Record<string, string> = {
    [locale?.myAccountOptions?.myProfile]: MY_ACCOUNT_PROFILE,
    [locale?.myAccountOptions?.accountSettings]: MY_PROFILE_ACCOUNT_SETTINGS,
    [locale?.myAccountOptions?.logOut]: LOGOUT,
  };

  const ProviderNotApprovedMyAccountRoutes: Record<string, string> = {
    [locale?.myAccountOptions.logOut]: LOGOUT,
  };

  const ProvidersRoutes: Record<string, string> = {
    [locale?.providersOptions?.myProviders]: PROVIDERS,
    [locale?.providersOptions?.pastProviders]: PROVIDERS_PAST_PROVIDERS,
    [locale?.providersOptions?.findAProvider]: PROVIDERS_FIND_A_PROVIDER,
  };

  const AppointmentsRoutes: Record<string, string> = {
    [locale?.appointmentsOptions?.myAppointments]: MY_APPOINTMENTS,
    [locale?.appointmentsOptions
      ?.scheduleAnAppointment]: `${MY_APPOINTMENTS_SCHEDULE_AN_APPOINTMENT}/${FIND_A_PROVIDER}`,
    ['Receipts history']: `${MY_ACCOUNT_PROFILE}/${PATIENT_PROFILE_ROUTES.MY_RECEIPTS_HISTORY}`,
  };

  const AdminProductsRoutes: Record<string, string> = {
    [transformNavBarOptionsToLowercase(locale?.products ?? 'Products')]:
      SUPER_ADMIN_PRODUCTS,
    [transformNavBarOptionsToLowercase(locale?.testKits ?? 'Test Kits')]:
      SUPER_ADMIN_TESTS,
    [transformNavBarOptionsToLowercase(locale?.orders ?? 'Orders')]:
      SUPER_ADMIN_ORDERS,
    [transformNavBarOptionsToLowercase(locale?.payments ?? 'Payments')]:
      SUPER_ADMIN_PAYMENTS,
  };

  const AdminProductsPermissions: Record<string, string> = {
    [transformNavBarOptionsToLowercase(locale?.products ?? 'Products')]:
      AdminPermissionType.ViewProductsInfo,
    [transformNavBarOptionsToLowercase(locale?.testKits ?? 'Test Kits')]:
      AdminPermissionType.ViewTestKitsInfo,
    [transformNavBarOptionsToLowercase(locale?.orders ?? 'Orders')]:
      AdminPermissionType.ViewOrdersInfo,
    [transformNavBarOptionsToLowercase(locale?.payments ?? 'Payments')]:
      AdminPermissionType.ViewPaymentsInfo,
  };

  const AdminUsersRoutes: Record<string, string> = {
    [transformNavBarOptionsToLowercase(locale?.admins ?? 'Users')]:
      SUPER_ADMIN_ADMINS,
    [transformNavBarOptionsToLowercase(locale?.patients ?? 'Patients')]:
      SUPER_ADMIN_PATIENTS,
    [transformNavBarOptionsToLowercase(locale?.providers ?? 'Providers')]:
      SUPER_ADMIN_PROVIDERS,
  };

  const AdminUsersPermissions: Record<string, string> = {
    [transformNavBarOptionsToLowercase(locale?.admins ?? 'Users')]: '',
    [transformNavBarOptionsToLowercase(locale?.patients ?? 'Patients')]:
      AdminPermissionType.ViewPatientsInfo,
    [transformNavBarOptionsToLowercase(locale?.providers ?? 'Providers')]:
      AdminPermissionType.ViewProvidersInfo,
  };

  return {
    locale,
    loadingOptions: loading,
    MySkinOptions,
    ManageTestKitsOptions: selectedAccount
      ? [ManageTestKitsOptions[0]]
      : ManageTestKitsOptions,
    ProvidersOptions,
    ShopOptions,
    MyAccountOptions,
    MySkinRoutes,
    ManageTestKitsRoutes,
    ShopRoutes,
    MyAccountRoutes,
    ProvidersRoutes,
    ProviderMyAccountOptions,
    ProviderNotApprovedMyAccountOptions,
    ProviderMyAccountRoutes,
    AppointmentsRoutes,
    AppointmentsOptions,
    ProviderNotApprovedMyAccountRoutes,
    SuperAdminAccountOptions,
    SuperAdminAccountRoutes,
    AdminProductsOptions,
    AdminProductsRoutes,
    AdminProductsPermissions,
    AdminUsersOptions,
    AdminUsersRoutes,
    AdminUsersPermissions,
  };
};
